import React, { useState } from "react";
import {
  AnimatedSizeContainer,
  Button,
  InfoTooltip,
  LinkPreview,
  Modal,
  SimpleTooltipContent,
  Textarea,
  Tooltip,
  useMediaQuery,
} from "@panda0/panda0-ui";
import { AnimatePresence, motion } from "framer-motion";
import {
  Copy,
  Download,
  LinkIcon,
  Mail,
  Pencil,
  QrCodeIcon,
  X,
} from "lucide-react";

import { QRCode } from "@/ui/shared/qr-code";

// Link Sharing Tab Component
function LinkSharingTab({
  shortLink,
  jobDetails,
  copyLink,
}: {
  shortLink: string;
  jobDetails: any;
  copyLink: () => void;
}) {
  const { isMobile } = useMediaQuery();
  const [showEditOGModal, setShowEditOGModal] = useState(false);

  // State for the selected sharing method
  const [selectedAction, setSelectedAction] = useState<string | null>(null);

  // State for customization inputs
  const [message, setMessage] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  // Copy message to clipboard
  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(message);
      alert("Message copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy!", err);
    }
  };

  // Handle click on sharing buttons
  const handleActionClick = (action: string) => {
    setSelectedAction(action);

    // Set default message based on action type
    switch (action) {
      case "twitter":
        setMessage(`${jobDetails.title} Quiz - ${shortLink}`);
        break;
      case "facebook":
        setMessage(`Check out this ${jobDetails.title} Quiz: ${shortLink}`);
        break;
      case "linkedin":
        setMessage(
          `I'd like to share this ${jobDetails.title} opportunity: ${shortLink}`
        );
        break;
      case "email":
        setTitle(`${jobDetails.title} Technical Quiz`);
        setMessage(`Please complete the technical quiz at: ${shortLink}`);
        break;
      case "copyLink":
        copyLink();
        break;
      default:
        setMessage("");
    }
  };

  // Perform the actual sharing action
  const performShareAction = () => {
    switch (selectedAction) {
      case "twitter":
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`,
          "_blank"
        );
        break;
      case "facebook":
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shortLink)}&quote=${encodeURIComponent(message)}`,
          "_blank"
        );
        break;
      case "linkedin":
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shortLink)}`,
          "_blank"
        );
        break;
      case "email":
        window.open(
          `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(message)}`,
          "_blank"
        );
        break;
      case "copyText":
        copyText();
        break;
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col md:flex-row h-full gap-4">
        {/* Left side - now 50% of the screen (was 1/3) */}
        <div className="md:w-1/2 rounded-xl border border-neutral-200 bg-white p-6">
          <div className="space-y-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-neutral-700" />
              PandaQuiz Link
              <InfoTooltip
                content={
                  <SimpleTooltipContent
                    title="Share this link with candidates to allow them to take the PandaQuiz."
                    cta="Learn more about sharing options."
                    href="#"
                  />
                }
              />
            </h3>

            {/* Sharing buttons above preview */}
            <div className="flex flex-wrap gap-2">
              <Tooltip content="Show QR Code">
                <Button
                  variant={
                    selectedAction === "qrcode" ? "primary" : "secondary"
                  }
                  onClick={() => handleActionClick("qrcode")}
                  className="h-9 w-9 p-0 flex items-center justify-center"
                  icon={<QrCodeIcon className="size-4" />}
                />
              </Tooltip>
              <Tooltip content="Copy Link">
                <Button
                  variant={
                    selectedAction === "copyLink" ? "primary" : "secondary"
                  }
                  onClick={() => {
                    copyLink();
                    handleActionClick("copyLink");
                  }}
                  className="h-9 w-9 p-0 flex items-center justify-center"
                  icon={<Copy className="h-4 w-4" />}
                />
              </Tooltip>
              <Tooltip content="Email Link">
                <Button
                  variant={selectedAction === "email" ? "primary" : "secondary"}
                  onClick={() => handleActionClick("email")}
                  className="h-9 w-9 p-0 flex items-center justify-center"
                  icon={<Mail className="h-4 w-4" />}
                />
              </Tooltip>
              <Tooltip content="Share on LinkedIn">
                <Button
                  variant={
                    selectedAction === "linkedin" ? "primary" : "secondary"
                  }
                  onClick={() => handleActionClick("linkedin")}
                  className="h-9 w-9 p-0 flex items-center justify-center"
                  icon={
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                    </svg>
                  }
                />
              </Tooltip>
              <Tooltip content="Share on Twitter">
                <Button
                  variant={
                    selectedAction === "twitter" ? "primary" : "secondary"
                  }
                  onClick={() => handleActionClick("twitter")}
                  className="h-9 w-9 p-0 flex items-center justify-center"
                  icon={
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z" />
                    </svg>
                  }
                />
              </Tooltip>
              <Tooltip content="Share on Facebook">
                <Button
                  variant={
                    selectedAction === "facebook" ? "primary" : "secondary"
                  }
                  onClick={() => handleActionClick("facebook")}
                  className="h-9 w-9 p-0 flex items-center justify-center"
                  icon={
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.53-4.5-10.02-10-10.02z" />
                    </svg>
                  }
                />
              </Tooltip>
              <Tooltip content="Download QR Code">
                <Button
                  variant={
                    selectedAction === "downloadQr" ? "primary" : "secondary"
                  }
                  onClick={() => handleActionClick("downloadQr")}
                  className="h-9 w-9 p-0 flex items-center justify-center"
                  icon={<Download className="h-4 w-4" />}
                />
              </Tooltip>

              <Button
                variant="secondary"
                size="sm"
                text="Edit OpenGraph/SEO Details"
                onClick={() => setShowEditOGModal(true)}
                icon={<Pencil className="h-4 w-4" />}
              />
            </div>

            {/* LinkPreview with fixed aspect ratio */}
            <div className="relative w-full" style={{ maxWidth: '500px', margin: '0 auto' }}>
              <div className="aspect-w-16 aspect-h-9 relative mt-4">
                <LinkPreview defaultUrl={shortLink} />
              </div>
            </div>
          </div>
        </div>
        {/* Right side - 50% of the screen */}
        <div className="md:w-1/2 rounded-xl border border-neutral-200 bg-white p-6">
          {!selectedAction ? (
            <div className="h-full flex items-center justify-center text-neutral-500">
              <p>
                Select a sharing method from the left panel to customize your
                message
              </p>
            </div>
          ) : selectedAction === "qrcode" ? (
            <div className="flex flex-col items-center gap-6">
              <h3 className="text-lg font-medium">QR Code for PandaQuiz</h3>

              <div className="h-56 w-56 flex-shrink-0 bg-white p-4 rounded-xl shadow-sm flex items-center justify-center">
                <div className="h-48 w-48 flex items-center justify-center">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={shortLink}
                      initial={{ filter: "blur(2px)", opacity: 0.4 }}
                      animate={{ filter: "blur(0px)", opacity: 1 }}
                      exit={{ filter: "blur(2px)", opacity: 0.4 }}
                      transition={{ duration: 0.1 }}
                      className="relative flex size-full items-center justify-center"
                    >
                      <QRCode url={shortLink} scale={2.0} />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>

              <p className="text-sm text-neutral-600 max-w-md text-center">
                Use this QR code in printed materials or displays to provide
                quick access to the PandaQuiz. Candidates can scan it with their
                phone's camera to open the link directly.
              </p>
            </div>
          ) : selectedAction === "copyLink" ? (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Link Copied!</h3>
              <div className="p-3 bg-neutral-50 rounded-md border border-neutral-200">
                <p className="font-mono text-sm break-all">{shortLink}</p>
              </div>
              <p className="text-sm text-neutral-600">
                The link has been copied to your clipboard. You can now paste it
                anywhere you want.
              </p>
              <Button
                variant="secondary"
                onClick={copyLink}
                text="Copy Again"
                icon={<Copy className="h-4 w-4" />}
              />
            </div>
          ) : selectedAction === "downloadQr" ? (
            <div className="flex flex-col items-center gap-6">
              <h3 className="text-lg font-medium">Download QR Code</h3>
              <div className="h-56 w-56 flex-shrink-0 bg-white p-4 rounded-xl shadow-sm flex items-center justify-center">
                <div className="h-48 w-48 flex items-center justify-center" id="qrcode-container">
                  <QRCode url={shortLink} scale={2.0} />
                </div>
              </div>

              <p className="text-sm text-neutral-600">
                Download this QR code to use in your printed materials or
                digital content.
              </p>
              <Button
                variant="primary"
                text="Download QR Code"
                onClick={() => {
                  // Create a canvas element from the QR code
                  const qrElement = document.getElementById('qrcode-container')?.querySelector('svg');
                  if (!qrElement) return;

                  const canvas = document.createElement('canvas');
                  const ctx = canvas.getContext('2d');

                  // Set canvas dimensions
                  canvas.width = qrElement.width.baseVal.value;
                  canvas.height = qrElement.height.baseVal.value;

                  // Convert SVG to data URL
                  const svgData = new XMLSerializer().serializeToString(qrElement);
                  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
                  const url = URL.createObjectURL(svgBlob);

                  // Create image from SVG and draw to canvas
                  const img = new Image();
                  img.onload = () => {
                    if (!ctx) return;
                    ctx.fillStyle = 'white';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(img, 0, 0);

                    // Download the PNG
                    const pngUrl = canvas.toDataURL('image/png');
                    const downloadLink = document.createElement('a');
                    downloadLink.href = pngUrl;
                    downloadLink.download = `${jobDetails.title.replace(/\s+/g, '-')}-QR-Code.png`;
                    document.body.appendChild(downloadLink);
                    downloadLink.click();
                    document.body.removeChild(downloadLink);
                    URL.revokeObjectURL(url);
                  };
                  img.src = url;
                }}
                icon={<Download className="h-4 w-4" />}
              />
              <Button
                variant="secondary"
                text="Download as SVG"
                icon={<Download className="h-4 w-4" />}
                onClick={() => {
                  // Get the SVG element
                  const qrElement = document.getElementById('qrcode-container')?.querySelector('svg');
                  if (!qrElement) return;

                  // Clone the node to avoid modifying the displayed one
                  const svgClone = qrElement.cloneNode(true) as SVGElement;

                  // Set white background
                  const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
                  rect.setAttribute('width', '100%');
                  rect.setAttribute('height', '100%');
                  rect.setAttribute('fill', 'white');
                  svgClone.insertBefore(rect, svgClone.firstChild);

                  // Convert to string and create a blob
                  const svgData = new XMLSerializer().serializeToString(svgClone);
                  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
                  const url = URL.createObjectURL(svgBlob);

                  // Create download link
                  const downloadLink = document.createElement('a');
                  downloadLink.href = url;
                  downloadLink.download = `${jobDetails.title.replace(/\s+/g, '-')}-QR-Code.svg`;
                  document.body.appendChild(downloadLink);
                  downloadLink.click();
                  document.body.removeChild(downloadLink);
                  URL.revokeObjectURL(url);
                }}
              />
            </div>
          ) : (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">
                Share on{" "}
                {selectedAction === "twitter"
                  ? "Twitter"
                  : selectedAction === "facebook"
                    ? "Facebook"
                    : selectedAction === "linkedin"
                      ? "LinkedIn"
                      : "Email"}
              </h3>

              {/* Title field for Email */}
              {selectedAction === "email" && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium">Subject</label>
                  <Textarea
                    placeholder="Enter email subject..."
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>
              )}

              {/* Message field for all sharing methods */}
              <div className="space-y-2">
                <label className="block text-sm font-medium">
                  {selectedAction === "email" ? "Email Body" : "Message"}
                </label>
                <Textarea
                  placeholder={`Enter your ${selectedAction === "email" ? "email" : "message"}...`}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                />
                <p className="text-xs text-neutral-500">
                  The link {shortLink} will be included in your message.
                </p>
              </div>

              <Button
                variant="primary"
                text={`Share on ${selectedAction === "twitter"
                  ? "Twitter"
                  : selectedAction === "facebook"
                    ? "Facebook"
                    : selectedAction === "linkedin"
                      ? "LinkedIn"
                      : "Email"
                  }`}
                onClick={performShareAction}
              />

              <Button
                variant="secondary"
                text="Copy Text"
                icon={<Copy className="h-4 w-4" />}
                onClick={copyText}
              />
            </div>
          )}
        </div>
      </div>
      {/* Edit OG Modal */}
      <Modal
        fullScreen={false}
        preventDefaultClose={false}
        preventEscapeClose={false}
        showModal={showEditOGModal}
        setShowModal={setShowEditOGModal}
        className="flex flex-col"
        onClose={() => setShowEditOGModal(false)}
      >
        {/* Header and Form */}
        <div className="flex-1 overflow-auto px-6 py-4">
          <div className="flex items-center justify-between mb-6 border-b border-neutral-100 pb-4">
            <p className="text-lg font-semibold">Edit OpenGraph/SEO Details</p>
            <Button
              variant="secondary"
              onClick={() => setShowEditOGModal(false)}
              className="h-9 w-9 p-0 flex items-center justify-center"
              icon={<X className="size-4 text-neutral-700" />}
            />
          </div>

          <div className="space-y-4">
            {/* Title Field */}
            <div className="relative">
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Title
              </label>
              <div className="absolute top-0 right-0 text-xs text-neutral-500 p-1">
                {title.length}/75
              </div>
              <Textarea
                placeholder="Enter your title"
                maxLength={75}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="pt-6"
              />
            </div>

            {/* Description Field */}
            <div className="relative">
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Description
              </label>
              <div className="absolute top-0 right-0 text-xs text-neutral-500 p-1">
                {description.length}/300
              </div>
              <Textarea
                placeholder="Enter your description"
                maxLength={300}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="pt-6"
              />
            </div>
          </div>
        </div>

        <div className="border-t border-neutral-100 px-6 py-3 sticky bottom-0 bg-white z-10">
          <Button
            size="sm"
            text="SAVE"
            onClick={() => setShowEditOGModal(false)}
          />
        </div>
      </Modal>
    </div>
  );
}

export default React.memo(LinkSharingTab);
